/**
 * Risk Management Calculation Utilities
 * 
 * These functions provide calculations for position sizing, stop losses,
 * and risk-reward analysis for trading with and without leverage.
 */

/**
 * Calculate position size based on account size and risk parameters
 * 
 * @param {number} accountSize - Total account value
 * @param {number} riskPercentage - Percentage of account to risk (e.g. 1 for 1%)
 * @param {number} entryPrice - Entry price per share/contract
 * @param {number} stopLossPrice - Stop loss price per share/contract
 * @param {number} fixedRiskAmount - Optional fixed dollar amount to risk (overrides riskPercentage)
 * @returns {Object} Position sizing details
 */
export const calculatePositionSize = (accountSize, riskPercentage, entryPrice, stopLossPrice, fixedRiskAmount = null) => {
  // Validate inputs
  if (!accountSize || accountSize <= 0) throw new Error("Account size must be positive");
  if ((!riskPercentage || riskPercentage <= 0) && !fixedRiskAmount) throw new Error("Risk percentage must be positive or fixed risk must be provided");
  if (!entryPrice || entryPrice <= 0) throw new Error("Entry price must be positive");
  if (!stopLossPrice || stopLossPrice <= 0) throw new Error("Stop loss price must be positive");
  
  // Long position: entry > stop loss; Short position: entry < stop loss
  const isLong = entryPrice > stopLossPrice;
  
  // Calculate risk per share/contract
  const riskPerUnit = Math.abs(entryPrice - stopLossPrice);
  
  if (riskPerUnit === 0) throw new Error("Entry and stop loss prices cannot be the same");
  
  // Calculate total risk amount
  const riskAmount = fixedRiskAmount || (accountSize * (riskPercentage / 100));
  
  // Calculate position size
  const positionSizeInUnits = parseFloat((riskAmount / riskPerUnit).toFixed(8));
  const positionValue = positionSizeInUnits * entryPrice;
  const totalRisk = positionSizeInUnits * riskPerUnit;
  const actualRiskPercentage = (totalRisk / accountSize) * 100;
  
  return {
    direction: isLong ? 'long' : 'short',
    positionSizeInUnits,
    positionValue,
    totalRisk,
    riskPerUnit,
    actualRiskPercentage,
    effectiveRiskPercentage: actualRiskPercentage.toFixed(2),
    maxLoss: -totalRisk,
  };
};

/**
 * Calculate margin-based position size with leverage
 * 
 * @param {number} accountSize - Total account value
 * @param {number} riskPercentage - Percentage of account to risk (e.g. 1 for 1%)
 * @param {number} availableMargin - Available margin for this trade
 * @param {number} leverage - Leverage ratio (e.g. 10 for 10x)
 * @param {number} entryPrice - Entry price per share/contract
 * @param {number} stopLossPrice - Stop loss price per share/contract
 * @param {string} marginMode - 'isolated' or 'cross'
 * @returns {Object} Leveraged position sizing details
 */
export const calculateMarginPosition = (accountSize, riskPercentage, availableMargin, leverage, entryPrice, stopLossPrice, marginMode = 'isolated') => {
  // Validate inputs
  if (!accountSize || accountSize <= 0) throw new Error("Account size must be positive");
  if (!riskPercentage || riskPercentage <= 0) throw new Error("Risk percentage must be positive");
  if (!availableMargin || availableMargin <= 0) throw new Error("Available margin must be positive");
  if (!leverage || leverage <= 0) throw new Error("Leverage must be positive");
  if (!entryPrice || entryPrice <= 0) throw new Error("Entry price must be positive");
  if (!stopLossPrice || stopLossPrice <= 0) throw new Error("Stop loss price must be positive");
  
  // Long position: entry > stop loss; Short position: entry < stop loss
  const isLong = entryPrice > stopLossPrice;
  const direction = isLong ? 'long' : 'short';
  
  // Calculate base risk amount
  const maxRiskAmount = accountSize * (riskPercentage / 100);
  
  // Calculate risk per unit
  const riskPerUnit = Math.abs(entryPrice - stopLossPrice);
  
  if (riskPerUnit === 0) throw new Error("Entry and stop loss prices cannot be the same");
  
  // Calculate position size based on risk
  const positionSizeUnits = parseFloat((maxRiskAmount / riskPerUnit).toFixed(8));

  // Calculate position value
  const positionValue = positionSizeUnits * entryPrice;

  // Calculate margin required with leverage
  const marginRequired = positionValue / leverage;

  // Check if margin is sufficient
  const isMarginSufficient = marginRequired <= availableMargin;

  // Calculate maximum position size with available margin
  const maxPositionSizeByMargin = parseFloat(((availableMargin * leverage) / entryPrice).toFixed(8));
  
  // Determine actual position size (limited by either risk or margin)
  const actualPositionSize = isMarginSufficient 
    ? positionSizeUnits 
    : maxPositionSizeByMargin;
  
  // Calculate actual risk amount
  const actualRiskAmount = actualPositionSize * riskPerUnit;
  const actualRiskPercentage = (actualRiskAmount / accountSize) * 100;
  
  // Calculate liquidation price (simplified calculation)
  let liquidationPrice;
  if (marginMode === 'isolated') {
    // For isolated margin, liquidation is when position loses ~margin value
    const maxLossPercentage = 100 - (100 / leverage) * 0.8; // 80% of margin (safety buffer)
    if (isLong) {
      liquidationPrice = entryPrice * (1 - maxLossPercentage / 100);
    } else {
      liquidationPrice = entryPrice * (1 + maxLossPercentage / 100);
    }
  } else {
    // For cross margin, we use a simpler calculation
    // This is a simplified version, real exchanges have more complex formulas
    const buffer = 0.8; // Safety buffer
    if (isLong) {
      liquidationPrice = entryPrice - (entryPrice * buffer / leverage);
    } else {
      liquidationPrice = entryPrice + (entryPrice * buffer / leverage);
    }
  }
  
  // Calculate distances as percentages
  const stopDistance = (Math.abs(entryPrice - stopLossPrice) / entryPrice) * 100;
  const liquidationDistance = (Math.abs(entryPrice - liquidationPrice) / entryPrice) * 100;
  
  return {
    direction,
    maxRiskAmount,
    positionSizeUnits: actualPositionSize,
    positionValue: actualPositionSize * entryPrice,
    leveragedExposure: actualPositionSize * entryPrice,
    marginRequired,
    riskPerUnit,
    actualRiskAmount,
    actualRiskPercentage,
    maxPositionByRisk: positionSizeUnits,
    maxPositionByMargin: maxPositionSizeByMargin,
    limitingFactor: positionSizeUnits <= maxPositionSizeByMargin ? 'Risk' : 'Margin',
    isMarginSufficient,
    liquidationPrice,
    stopDistance,
    liquidationDistance
  };
};

/**
 * Calculate stop loss price based on entry and volatility/percentage
 * 
 * @param {number} entryPrice - Entry price per share/contract
 * @param {boolean} isLong - Whether position is long or short
 * @param {number} atrValue - Average True Range value (volatility)
 * @param {number} atrMultiplier - Multiplier for ATR (e.g. 2 = 2xATR)
 * @param {number} fixedPercentage - Fixed percentage for stop placement
 * @returns {Object} Stop loss calculation details
 */
export const calculateStopLoss = (entryPrice, isLong, atrValue = null, atrMultiplier = 2, fixedPercentage = null) => {
  // Validate inputs
  if (!entryPrice || entryPrice <= 0) throw new Error("Entry price must be positive");
  
  let stopLossPrice;
  let method = '';
  let distance = 0;
  
  // ATR-based stop loss
  if (atrValue && atrValue > 0) {
    const atrDistance = atrValue * atrMultiplier;
    stopLossPrice = isLong 
      ? entryPrice - atrDistance 
      : entryPrice + atrDistance;
    method = `${atrMultiplier}x ATR (${atrValue.toFixed(2)})`;
    distance = atrDistance;
  } 
  // Percentage-based stop loss
  else if (fixedPercentage && fixedPercentage > 0) {
    const percentDistance = entryPrice * (fixedPercentage / 100);
    stopLossPrice = isLong 
      ? entryPrice - percentDistance 
      : entryPrice + percentDistance;
    method = `${fixedPercentage}%`;
    distance = percentDistance;
  } 
  // Default to 5% if no method specified
  else {
    const percentDistance = entryPrice * 0.05; // Default 5%
    stopLossPrice = isLong 
      ? entryPrice - percentDistance 
      : entryPrice + percentDistance;
    method = '5% (default)';
    distance = percentDistance;
  }
  
  // Ensure stop loss is positive
  stopLossPrice = Math.max(stopLossPrice, 0.01);
  
  const distancePercentage = (Math.abs(entryPrice - stopLossPrice) / entryPrice) * 100;
  
  return {
    stopLossPrice,
    method,
    distance,
    distancePercentage,
    distanceFormatted: distancePercentage.toFixed(2) + '%',
  };
};

/**
 * Calculate take profit levels based on risk-reward ratios
 * 
 * @param {number} entryPrice - Entry price per share/contract
 * @param {number} stopLossPrice - Stop loss price per share/contract
 * @param {Array<number>} riskRewardRatios - Array of risk-reward ratios (e.g. [1, 2, 3])
 * @returns {Array<Object>} Take profit levels and details
 */
export const calculateTakeProfitLevels = (entryPrice, stopLossPrice, riskRewardRatios = [1, 2, 3]) => {
  // Validate inputs
  if (!entryPrice || entryPrice <= 0) throw new Error("Entry price must be positive");
  if (!stopLossPrice || stopLossPrice <= 0) throw new Error("Stop loss price must be positive");
  
  const isLong = entryPrice > stopLossPrice;
  const riskDistance = Math.abs(entryPrice - stopLossPrice);
  
  return riskRewardRatios.map(ratio => {
    const rewardDistance = riskDistance * ratio;
    const takeProfitPrice = isLong 
      ? entryPrice + rewardDistance 
      : entryPrice - rewardDistance;
    
    const priceMovement = Math.abs(takeProfitPrice - entryPrice);
    const movementPercentage = (priceMovement / entryPrice) * 100;
    
    return {
      ratio,
      takeProfitPrice,
      rewardDistance,
      priceMovement,
      movementPercentage,
      movementFormatted: movementPercentage.toFixed(2) + '%',
    };
  });
};

/**
 * Calculate expected value of a trading strategy
 * 
 * @param {number} winRate - Percentage chance of winning (e.g. 50 for 50%)
 * @param {number} rewardRatio - Reward to risk ratio (e.g. 2 for 2R)
 * @returns {Object} Expected value details
 */
export const calculateExpectedValue = (winRate, rewardRatio) => {
  // Validate inputs
  if (winRate < 0 || winRate > 100) throw new Error("Win rate must be between 0 and 100");
  if (rewardRatio <= 0) throw new Error("Reward ratio must be positive");
  
  const winRateDecimal = winRate / 100;
  const lossRateDecimal = 1 - winRateDecimal;
  
  // Expected value = (Win Rate × Reward) - (Loss Rate × Risk)
  // For every 1 unit risked, the reward is rewardRatio units
  const expectedValue = (winRateDecimal * rewardRatio) - lossRateDecimal;
  
  // Break-even win rate
  const breakEvenWinRate = 1 / (1 + rewardRatio);
  const breakEvenWinRatePercentage = breakEvenWinRate * 100;
  
  // Profit factor = (Win Rate × Reward) ÷ (Loss Rate × Risk)
  const profitFactor = (winRateDecimal * rewardRatio) / lossRateDecimal;
  
  return {
    expectedValue,
    expectedValueFormatted: expectedValue.toFixed(2) + 'R',
    breakEvenWinRate,
    breakEvenWinRatePercentage,
    profitFactor,
    isPositiveExpectancy: expectedValue > 0,
  };
};

/**
 * Calculate required win rate for a given risk-reward ratio to be profitable
 * 
 * @param {number} riskRewardRatio - Risk to reward ratio
 * @returns {number} Required win rate percentage
 */
export const calculateRequiredWinRate = (riskRewardRatio) => {
  // Validate input
  if (riskRewardRatio <= 0) throw new Error("Risk-reward ratio must be positive");
  
  // Required win rate = 1 / (1 + R:R)
  const requiredWinRate = 1 / (1 + riskRewardRatio);
  
  return requiredWinRate * 100; // Convert to percentage
};

/**
 * Calculate Kelly criterion for optimal position sizing
 * 
 * @param {number} winRate - Percentage chance of winning (e.g. 50 for 50%)
 * @param {number} rewardRatio - Reward to risk ratio
 * @returns {number} Kelly percentage (optimal allocation)
 */
export const calculateKellyCriterion = (winRate, rewardRatio) => {
  // Validate inputs
  if (winRate < 0 || winRate > 100) throw new Error("Win rate must be between 0 and 100");
  if (rewardRatio <= 0) throw new Error("Reward ratio must be positive");
  
  const winRateDecimal = winRate / 100;
  
  // Kelly formula: K% = W - [(1 - W) / R]
  // Where W = Win rate, R = Win/loss ratio (reward ratio)
  const kelly = winRateDecimal - ((1 - winRateDecimal) / rewardRatio);
  
  // Kelly can be negative for negative expectancy systems
  // For practical purposes, we'll return 0 in those cases
  const adjustedKelly = Math.max(0, kelly);
  
  return adjustedKelly * 100; // Convert to percentage
};

/**
 * Format a price or dollar value with smart decimal precision based on magnitude.
 * < 0.01  → 6 decimal places (e.g. forex pip values)
 * < 1     → 4 decimal places
 * >= 1    → 2 decimal places
 *
 * @param {number} value - The numeric value to format
 * @returns {string} Locale-formatted string with appropriate decimal places
 */
export const formatPrice = (value) => {
  if (value < 0.01) {
    return value.toLocaleString(undefined, { minimumFractionDigits: 6, maximumFractionDigits: 6 });
  }
  if (value < 1) {
    return value.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  }
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Convert a win rate percentage to odds expression
 * 
 * @param {number} winRatePercentage - Win rate as a percentage (e.g., 60 for 60%)
 * @returns {string} Odds expression (e.g., "3:2")
 */
export const convertWinRateToOdds = (winRatePercentage) => {
  if (winRatePercentage <= 0 || winRatePercentage >= 100) {
    return winRatePercentage === 100 ? "∞:0" : "0:∞";
  }

  const winRate = winRatePercentage / 100;
  const lossRate = 1 - winRate;
  
  // Find the smallest integer ratio (to make it readable)
  const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
  
  // Convert to integers out of 100 for better precision
  let wins = Math.round(winRate * 100);
  let losses = Math.round(lossRate * 100);
  
  // Find greatest common divisor
  const divisor = gcd(wins, losses);
  
  // Simplify the ratio
  wins = wins / divisor;
  losses = losses / divisor;
  
  return `${wins}:${losses}`;
};